<script>
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import fetchData from '../../api.js';
	import UserStore from '../../stores/user';
	import { money, formatDate, VAT_PERCENT } from '../../lib/format';
	import AppShell from '../../components/AppShell.svelte';

	// a date as yyyy-mm-dd in local time (date inputs and order dates use this form)
	function ymd(d) {
		return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
	}
	function monthsAgo(n) {
		const d = new Date();
		d.setMonth(d.getMonth() - n);
		return ymd(d);
	}
	const todayYmd = ymd(new Date());
	const PRESETS = [
		{ label: 'החודש', from: () => todayYmd.slice(0, 8) + '01' },
		{ label: '3 חודשים', from: () => monthsAgo(3) },
		{ label: 'חצי שנה', from: () => monthsAgo(6) },
		{ label: 'השנה', from: () => todayYmd.slice(0, 5) + '01-01' }
	];

	let orders = [];
	let agents = [];
	let loading = true;
	let q = '';
	let from = monthsAgo(3);
	let to = todayYmd;

	const beforeVat = (n) => Math.round((n / (1 + VAT_PERCENT / 100)) * 100) / 100;
	const day = (d) => (d ? d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(2, 4) : '');

	$: isAdmin = $UserStore.role === 'admin';
	$: inRange = orders.filter((o) => {
		const d = String(o.date_time || '').slice(0, 10);
		return (!from || d >= from) && (!to || d <= to);
	});
	$: shown = inRange.filter((o) => !q || (o.customer_name || '').includes(q) || String(o.order_number).includes(q) || (o.agent_name || '').includes(q) || (o.brands || '').toLowerCase().includes(q.toLowerCase()));

	// sales per agent in the chosen dates; the admin also sees agents who sold nothing
	$: summary = (() => {
		const map = new Map();
		for (const a of agents) map.set(String(a.id), { name: a.name, orders: 0, units: 0, total: 0 });
		for (const o of inRange) {
			const key = String(o.user_id || 0);
			if (!map.has(key)) map.set(key, { name: o.agent_name || 'ללא סוכן', orders: 0, units: 0, total: 0 });
			const s = map.get(key);
			s.orders += 1;
			s.units += Number(o.units) || 0;
			s.total += Number(o.total) || 0;
		}
		return [...map.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
	})();
	$: sums = summary.reduce((t, s) => ({ orders: t.orders + s.orders, units: t.units + s.units, total: t.total + s.total }), { orders: 0, units: 0, total: 0 });

	onMount(async () => {
		if (!$UserStore || !$UserStore.data) {
			push('/login');
			return;
		}
		const [res, users] = await Promise.all([
			fetchData({ action: 'agents_get_orders', token: $UserStore.data }),
			$UserStore.role === 'admin' ? fetchData({ action: 'agents_users_list', token: $UserStore.data }) : null
		]);
		orders = Object.values((res && res.data) || {}).sort((a, b) => Number(b.id) - Number(a.id));
		agents = ((users && users.data) || []).filter((u) => u.role === 'agent' && Number(u.active) === 1);
		loading = false;
	});
</script>

<AppShell title="הזמנות">
	<section class="range">
		<span class="label">תאריכים</span>
		<div class="seg">
			{#each PRESETS as p}
				<button class:on={from === p.from() && to === todayYmd} on:click={() => ((from = p.from()), (to = todayYmd))}>{p.label}</button>
			{/each}
		</div>
		<label class="date"><span>מ-</span><input type="date" bind:value={from} max={to || todayYmd} /></label>
		<label class="date"><span>עד</span><input type="date" bind:value={to} min={from} max={todayYmd} /></label>
	</section>

	{#if loading}
		<div class="spinner" />
	{:else}
		<section class="sales">
			<h2>{isAdmin ? 'מכירות לפי סוכן' : 'המכירות שלי'} <small class="muted">{day(from)} – {day(to)}</small></h2>
			{#if !summary.length}
				<p class="muted none">אין הזמנות בתאריכים האלה.</p>
			{:else}
				<div class="stable">
					<div class="shead"><span>סוכן</span><span>הזמנות</span><span>יחידות</span><span>לפני מע״מ</span><span>כולל מע״מ</span></div>
					{#each summary as s}
						<div class="srow" class:zero={!s.orders}>
							<span class="name">{s.name}</span>
							<span>{s.orders}</span>
							<span>{s.units}</span>
							<span class="money">{money(beforeVat(s.total))}</span>
							<span class="muted">{money(Math.round(s.total * 100) / 100)}</span>
						</div>
					{/each}
					{#if summary.length > 1}
						<div class="srow total">
							<span>סה״כ</span>
							<span>{sums.orders}</span>
							<span>{sums.units}</span>
							<span class="money">{money(beforeVat(sums.total))}</span>
							<span>{money(Math.round(sums.total * 100) / 100)}</span>
						</div>
					{/if}
				</div>
			{/if}
		</section>

		<div class="top">
			<p class="muted">הזמנות שנשלחו לווריפון בתאריכים שנבחרו. לוחצים על הזמנה כדי לראות את הפריטים.</p>
			<label class="search small"><input type="search" placeholder="חיפוש לקוח, מותג או מספר הזמנה" bind:value={q} /></label>
		</div>

		{#if !shown.length}
			<div class="empty-state"><b>אין הזמנות{q ? ' שמתאימות לחיפוש' : ' בתאריכים האלה'}</b></div>
		{:else}
			<div class="table">
				<div class="head">
					<span>מספר הזמנה</span><span>לקוח</span><span>מותג</span><span>תאריך</span><span>יחידות</span><span>סה״כ</span>
				</div>
				{#each shown as o (o.id)}
					<button class="row" on:click={() => push('/agents/carts/' + o.id)}>
						<span class="num">{o.order_number}</span>
						<span class="name">{o.customer_name || '—'}{#if isAdmin}<small class="muted agent">{o.agent_name || 'לפני מערכת המשתמשים'}</small>{/if}</span>
						<span class="brand">{o.brands || '—'}</span>
						<span class="muted">{formatDate(o.date_time)}</span>
						<span>{Number(o.units) || 0}</span>
						<span class="money">{money(o.total)}</span>
					</button>
				{/each}
			</div>
		{/if}
	{/if}
</AppShell>

<style>
	.range { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
	.range .label { font-size: 15px; color: var(--muted); }
	.date { display: flex; align-items: center; gap: 6px; font-size: 15px; color: var(--ink-2); }
	.date input { height: 50px; border: 1px solid var(--line-strong); border-radius: 12px; padding: 0 12px; font-size: 16px; background: var(--surface); }
	.sales { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 16px 20px 8px; margin-bottom: 20px; }
	.sales h2 { margin: 0 0 10px; font-size: 19px; }
	.sales h2 small { font-size: 15px; font-weight: 400; margin-inline-start: 6px; }
	.none { margin: 4px 0 12px; }
	.shead, .srow { display: grid; grid-template-columns: 1fr 90px 90px 140px 140px; gap: 12px; align-items: center; }
	.shead { height: 36px; color: var(--muted); font-size: 14px; border-bottom: 1px solid var(--line); }
	.srow { min-height: 50px; border-bottom: 1px solid var(--line); font-size: 17px; }
	.srow:last-child { border-bottom: 0; }
	.srow.zero { color: var(--muted); }
	.srow.total { font-weight: 800; border-top: 2px solid var(--ink); }
	.top { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
	.top p { margin: 0; }
	.search.small { flex: 0 1 340px; }
	.table { display: block; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden; }
	.head, .row { display: grid; grid-template-columns: 120px 1fr 150px 160px 80px 120px; gap: 12px; align-items: center; padding: 0 20px; }
	.head { height: 48px; color: var(--muted); font-size: 14px; border-bottom: 1px solid var(--line); }
	.row { width: 100%; min-height: 64px; border: 0; border-bottom: 1px solid var(--line); background: none; text-align: start; font-size: 17px; }
	.row:last-child { border-bottom: 0; }
	.row:active { background: var(--bg); }
	.num { font-weight: 800; }
	.name { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.agent { display: block; font-size: 13px; font-weight: 400; }
	.money { font-weight: 700; }
	.brand { font-weight: 700; font-size: 15px; }
	@media (max-width: 1000px) {
		.head, .row { grid-template-columns: 90px 1fr 110px 110px; }
		.head span:nth-child(4), .head span:nth-child(5), .row span:nth-child(4), .row span:nth-child(5) { display: none; }
		.shead, .srow { grid-template-columns: 1fr 70px 120px 120px; }
		.shead span:nth-child(3), .srow span:nth-child(3) { display: none; }
	}
</style>
